import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { Subscription, filter, take, takeUntil } from 'rxjs';

import { User } from '../../../../../auth/models/user.model';
import { LoginService } from '../../../../../auth/services/login.service';
import { Company } from '../../../../../models/company.model';
import { Pagination, parsePagination } from '../../../../../models/pagination.model';
import { CompanyService } from '../../../../../services/company.service';
import { CurrentDateService } from '../../../../../services/current-date.service';
import { startOfDay } from '../../../../../utilities/date';
import { TableFilters } from '../../../../../utilities/table-filters';
import { Contract } from '../../../contracts/models/contract.model';
import { SlotRequestStatus, SlotsRequest } from '../../../../models/slots-request.model';
import { SlotRequestService } from '../../../../services/slot-request.service';

@Component({
  selector: 'ag-slots-requests-table',
  templateUrl: './slots-requests-table.component.html',
  styleUrls: ['./slots-requests-table.component.scss']
})
export class SlotsRequestsTableComponent extends TableFilters implements OnInit, OnDestroy {

  @ViewChild('editModal', { static: true }) private readonly editModal: TemplateRef<any>;

  @Input() public company: Company;
  @Input() public compact: boolean = false;
  @Input() public negotiationId?: number;
  @Input() public contractId?: number;
  @Input() public contract?: Contract;
  @Input() public preLoadedSlotsRequests?: SlotsRequest[];

  @Output('load') readonly onLoad = new EventEmitter();

  public processing: boolean;
  public exporting: boolean = false;
  public readonlyUser: boolean;
  /** Current {@link User} */
  public user: User;
  public requests: SlotsRequest[];
  public contractPendingQuantity: number = 0;

  public editForm: UntypedFormGroup;
  public editProcessing: boolean = false;
  public today: Date;
  public editMaxQuantity: number | null = null;
  private editingRequest: SlotsRequest;
  private modalRef: BsModalRef;

  private pusherSubscription: Subscription;

  constructor(
    private slotRequestService: SlotRequestService,
    private loginService: LoginService,
    private companyService: CompanyService,
    private modalService: BsModalService,
    private currentDate: CurrentDateService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    public route: ActivatedRoute,
    public router: Router
  ) {
    super(route, router);
  }

  ngOnInit(): void {
    this.loginService.getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        if (!user) return;
        this.user = user;
      });

    this.companyService.watchAccount()
      .pipe(takeUntil(this.destroy$))
      .subscribe(account => {
        if (!account) return;

        // Account changed
        this.readonlyUser = !account.is.logistics && !account.is.commercial && !account.is.commercial_with_users;
      });

    // Filters based on URL
    this.onFiltersChange = this.loadData;
    this.setupFilters();

    if (this.contract) {
      this.loadContractSummary();
    }
  }

  private loadContractSummary(): void {
    this.slotRequestService.summary(this.company.id, {
      'filters[contract][id]': this.contract.id,
      validity: 'on_date'
    }).pipe(takeUntil(this.destroy$))
      .subscribe(summary => {
        this.contractPendingQuantity = summary.pending_quantity;
      });
  }

  public isRecipient(item: SlotsRequest): boolean {
    return item.recipient.id === this.company.id;
  }

  get pendingCuposTotal(): number {
    return this.contractPendingQuantity;
  }

  get isContractCuposExceeded(): boolean {
    if (!this.contract || this.pendingCuposTotal === 0) return false;
    return this.pendingCuposTotal > this.contract.assignableSlots;
  }

  get exceedingRequestIds(): Set<number> {
    if (!this.requests?.length) return new Set();

    const active = this.requests.filter(r =>
      !r.expired &&
      r.status?.id !== SlotRequestStatus.RETIRED &&
      r.status?.id !== SlotRequestStatus.REJECTED
    );

    // Agrupar por contrato para poder calcular acumulado por cada uno
    const byContract = new Map<number, SlotsRequest[]>();
    for (const req of active) {
      const contractId = req.contract?.id;
      if (contractId == null) continue;
      if (!byContract.has(contractId)) byContract.set(contractId, []);
      byContract.get(contractId).push(req);
    }

    const ids = new Set<number>();

    for (const [contractId, reqs] of byContract) {
      // Si se pasó el contrato como Input y coincide, usar su límite; si no, usar el embebido en la solicitud
      const limit = (this.contract?.id === contractId)
        ? this.contract.assignableSlots
        : reqs[0]?.contract?.assignableSlots;

      if (!limit) continue;

      // Primero por fecha de ejecución asc, luego por created_at asc como desempate
      const sorted = [...reqs].sort((a, b) => {
        const dateDiff = new Date(a.date).getTime() - new Date(b.date).getTime();
        if (dateDiff !== 0) return dateDiff;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });

      let accumulated = 0;
      for (const req of sorted) {
        const effectiveQty = (req.status?.id === SlotRequestStatus.ASSIGNED_PARTIALLY && req.rejected > 0)
          ? req.assignedQuantity
          : req.quantity;

        accumulated += effectiveQty;
        if (accumulated > limit) {
          ids.add(req.id);
        }
      }
    }

    return ids;
  }

  public canRetire(slotRequest: SlotsRequest): boolean {
    return !slotRequest.expired && !this.isRecipient(slotRequest) && slotRequest.status?.id === SlotRequestStatus.PENDING;
  }

  public canAccept(slotRequest: SlotsRequest): boolean {
    if (slotRequest.expired || !this.isRecipient(slotRequest)) return false;
    if (!slotRequest.contract && !slotRequest.negotiation) return false;
    if (slotRequest.status?.id === SlotRequestStatus.PENDING) return true;
    if (slotRequest.status?.id === SlotRequestStatus.ASSIGNED_PARTIALLY) {
      // No mostrar si hubo devoluciones o rechazos — solo informativo a partir de ese punto
      return !slotRequest.rejected && !slotRequest.hasReturnedBatches;
    }
    return false;
  }

  public canReject(slotRequest: SlotsRequest): boolean {
    if (slotRequest.expired || !this.isRecipient(slotRequest)) return false;
    if (slotRequest.status?.id === SlotRequestStatus.PENDING) return true;
    if (slotRequest.status?.id === SlotRequestStatus.ASSIGNED_PARTIALLY) {
      // Mostrar solo si aún no se rechazaron los cupos restantes
      return !slotRequest.rejected;
    }
    return false;
  }

  public canDelete(slotRequest: SlotsRequest): boolean {
    return slotRequest.company.id === this.company.id && slotRequest.status?.id === SlotRequestStatus.PENDING;
  }

  public canEdit(slotRequest: SlotsRequest): boolean {
    return !slotRequest.expired
      && !this.isRecipient(slotRequest)
      && slotRequest.status?.id === SlotRequestStatus.PENDING
      && slotRequest.assigned === 0;
  }

  private calculateMaxQuantity(slotRequest: SlotsRequest): number | null {
    try {
      if (slotRequest.contract) return slotRequest.contract.assignableSlots ?? null;
      if (slotRequest.negotiation) return slotRequest.negotiation.assignableSlots ?? null;
    } catch { }
    return null;
  }

  public openEditModal(slotRequest: SlotsRequest): void {
    this.editingRequest = slotRequest;
    this.today = startOfDay(this.currentDate.get());
    this.editMaxQuantity = this.calculateMaxQuantity(slotRequest);

    const quantityValidators = [Validators.required, Validators.min(1)];
    if (this.editMaxQuantity != null) quantityValidators.push(Validators.max(this.editMaxQuantity));

    this.editForm = new UntypedFormGroup({
      quantity: new UntypedFormControl(slotRequest.quantity, quantityValidators),
      date: new UntypedFormControl(new Date(slotRequest.date), Validators.required),
      observations: new UntypedFormControl(slotRequest.observations || '')
    });

    this.modalRef = this.modalService.show(this.editModal, { class: 'modal-md' });
  }

  public submitEdit(): void {
    if (this.editForm.invalid) return;

    this.editProcessing = true;
    const { quantity, date, observations } = this.editForm.value;

    this.slotRequestService.update(this.company.id, this.editingRequest.id, { quantity, date, observations })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.editProcessing = false;
          this.modalRef?.hide();
        },
        error: () => {
          this.editProcessing = false;
        }
      });
  }

  public closeEditModal(): void {
    this.modalRef?.hide();
  }

  private loadData(): void {
    if (!this.company) return;

    if (!this.user) {
      // Defer until user is available, then retry
      this.loginService.getCurrentUser()
        .pipe(filter(u => !!u), take(1), takeUntil(this.destroy$))
        .subscribe(() => this.loadData());
      return;
    }

    this.processing = true;

    if (this.pusherSubscription) this.pusherSubscription.unsubscribe();

    const queryParams = this.filters || this.route.snapshot.queryParams;
    let observable = this.slotRequestService.watch(this.company.id, this.user.id, queryParams);

    if (this.negotiationId) {
      observable = this.slotRequestService.watch(this.company.id, this.user.id, {
        negotiation_id: this.negotiationId,
        // validity: 'on_date'
      });
    } else if (this.contractId) {
      observable = this.slotRequestService.watch(this.company.id, this.user.id, {
        'filters[contract][id]': this.contractId,
        // validity: 'on_date'
      });
    }

    this.pusherSubscription = observable.subscribe({
      next: response => {
        if (!response) return;

        this.dataLoaded(response.body, parsePagination(response.headers));
        this.processing = false;
      },
      error: () => {
        this.processing = false;
      }
    });
  }

  public exportToXLSX(): void {
    this.exporting = true;
    const queryParams = this.route.parent?.snapshot.queryParams || {};
    this.slotRequestService.export(this.company.id, queryParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.exporting = false;
          this.toastrService.success(
            this.translateService.instant('GLOBAL.EXPORT_XLS_SUCCESS'),
            undefined,
            { timeOut: 10000, enableHtml: true }
          );
        },
        error: (err) => {
          this.exporting = false;
          const status = err?.status;
          if (status === 401 || status === 403) {
            this.toastrService.error(
              this.translateService.instant('GLOBAL.APP_ERROR_' + status)
            );
          } else {
            this.toastrService.error(
              this.translateService.instant('GLOBAL.APP_ERROR_DEFAULT')
            );
          }
        }
      });
  }

  public reject(observations: string, slotRequest: SlotsRequest): void {
    this.slotRequestService.reject(this.company.id, slotRequest.id, observations)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => { });
  }

  public retire(observations: string, slotRequest: SlotsRequest): void {
    this.slotRequestService.retire(this.company.id, slotRequest.id, observations)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => { });
  }

  public delete(observations: string, slotRequest: SlotsRequest): void {
    this.slotRequestService.delete(this.company.id, slotRequest.id, observations)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => { });
  }

  public getStatusClass(statusId: number): string {
    const statusClassMap = {
      [SlotRequestStatus.UNKNOWN]: 'text-danger',
      [SlotRequestStatus.PENDING]: 'text-warning',
      [SlotRequestStatus.EXPIRED]: 'text-danger',
      [SlotRequestStatus.ASSIGNED]: 'text-success',
      [SlotRequestStatus.ASSIGNED_PARTIALLY]: 'text-warning',
      [SlotRequestStatus.RETIRED]: 'text-danger',
      [SlotRequestStatus.REJECTED]: 'text-danger'
    };

    return statusClassMap[statusId] ?? '';
  }

  /**
   * Devuelve la clase CSS correcta para el estado de una solicitud,
   * teniendo en cuenta rechazos y devoluciones parciales.
   */
  public getItemStatusClass(item: SlotsRequest): string {
    if (item.status?.id === SlotRequestStatus.REJECTED) return 'text-danger';
    if (item.hasRejected || item.hasReturnedBatches) return 'text-danger';
    return this.getStatusClass(item.status?.id);
  }

  /**
   * Devuelve la clave i18n del label de estado según la combinación
   * de rechazos y devoluciones parciales presentes en la solicitud.
   */
  public getStatusLabel(item: SlotsRequest): string {
    if (item.status?.id === SlotRequestStatus.REJECTED) {
      return item.status.locale_name;
    }
    const hasRejected = item.hasRejected;
    const hasReturned = item.hasReturnedBatches;
    if (hasRejected && hasReturned) return 'SLOTS.STATUS.ASSIGNED_PARTIALLY_WITH_REJECTED_AND_RETURNED';
    if (hasRejected) return 'SLOTS.STATUS.ASSIGNED_PARTIALLY_WITH_REJECTIONS';
    if (hasReturned) return 'SLOTS.STATUS.ASSIGNED_PARTIALLY_WITH_REJECTED';
    return item.status?.locale_name ?? '';
  }

  private dataLoaded(data: SlotsRequest[], pagination?: Pagination): void {
    // Ordenar por date ASC + created_at ASC para que el orden visual coincida
    // con la lógica de acumulación de exceedingRequestIds
    this.requests = [...data].sort((a, b) => {
      const dateDiff = new Date(b.date).getTime() - new Date(a.date).getTime(); // DESC: fecha más reciente primero
      if (dateDiff !== 0) return dateDiff;
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime(); // ASC: creado antes primero (mismo día)
    });

    if (this.contract) {
      this.loadContractSummary();
    }

    this.onLoad.emit({
      data: this.requests,
      pagination: pagination
    });
  }

  ngOnDestroy(): void {
    if (this.pusherSubscription) this.pusherSubscription.unsubscribe();

    super.ngOnDestroy();
  }
}
